import { logger as mainLogger } from "@klarify/telemetry";
import type { MicAccessVerdict, MicPermissionState } from "@klarify/types";
import { hasMediaDevices, hasNavigator, hasPermissions, orTimeout } from "..";

const logger = mainLogger.meta({ source: "Audio Devices" }).seal();

// SecurityError is deliberately absent: it covers insecure contexts and
// not-fully-active documents, which are not revocable user decisions.
const DENIAL_ERROR_NAMES = new Set([
	"NotAllowedError",
	"PermissionDeniedError",
]);

// Browsers check permission before hardware, so these prove the mic is
// missing (or the constraints unsatisfiable), never that access is blocked.
const NO_DEVICE_ERROR_NAMES = new Set([
	"NotFoundError",
	"DevicesNotFoundError",
	"OverconstrainedError",
]);

async function enumerateDevicesSafe(): Promise<MediaDeviceInfo[] | null> {
	return await orTimeout(navigator.mediaDevices.enumerateDevices(), 5000).catch(
		(err) => {
			logger.error(err, "Error enumerating devices");
			return null;
		},
	);
}

// Browsers that reject the "microphone" descriptor (pre-131 Firefox, older
// Safari) reject it on every query. Device refreshes call
// getMicPermissionState per enumeration, so log the expected fallback once
// per page instead of once per devicechange. Only descriptor rejections
// latch; a transient failure (e.g. a query timeout) must stay visible.
let unsupportedDescriptorLogged = false;

/**
 * Read-only microphone permission state. Never opens a stream and never
 * shows a browser prompt (BUG-1116). Without the Permissions API (older
 * Safari), a labeled audio input is the only "granted" signal, so an
 * unlabeled list reports "unknown" instead of prompting.
 *
 * Not authoritative for denial: device transitions can transiently report
 * "denied" while capture works fine, so callers that can run a gesture
 * confirm with requestMicAccess before acting on it.
 */
export async function getMicPermissionState(): Promise<MicPermissionState> {
	if (!(hasNavigator() && hasMediaDevices())) {
		return "unknown";
	}
	if (hasPermissions()) {
		try {
			const permission = await orTimeout(
				navigator.permissions.query({
					name: "microphone",
				}),
				5000,
			);
			logger.info("Mic permission state", { state: permission.state });
			return permission.state;
		} catch (e) {
			// Firefox rejects {name:"microphone"} queries; this is the expected
			// fallback path there, not an error.
			const unsupportedDescriptor =
				e instanceof TypeError ||
				(e instanceof Error && e.name === "NotSupportedError");
			if (!(unsupportedDescriptor && unsupportedDescriptorLogged)) {
				logger.warn(e, "Permission API query failed, falling back to labels");
			}
			if (unsupportedDescriptor) {
				unsupportedDescriptorLogged = true;
			}
		}
	}
	const devices = await enumerateDevicesSafe();
	const hasLabeledInput = devices?.some(
		(device) => device.kind === "audioinput" && device.label.length > 0,
	);
	return hasLabeledInput ? "granted" : "unknown";
}

/**
 * Open and immediately stop a mic stream to trigger the browser permission
 * prompt and expose device labels (Safari and Firefox hide them until a
 * capture succeeds in the frame, then reveal them for its lifetime, so
 * stopping this stream does not re-hide them). This turns the mic on for real — the browser shows its
 * mic-in-use indicator — so call it only from an explicit user action or
 * recording surface, never from passive page code (BUG-1116).
 *
 * This is also ground truth for denial: a real denial rejects instantly
 * with no prompt shown.
 *
 * The prompt is not bounded by our timeout: a user can answer it after this
 * function already returned false. That late grant resolves the probe, so
 * `onLateGrant` fires (after the late tracks are stopped) and callers can
 * refresh their device list instead of stranding an empty picker.
 */
export async function requestMicAccess(options?: {
	onLateGrant?: () => void;
}): Promise<MicAccessVerdict> {
	if (!hasMediaDevices()) {
		logger.info("requestMicAccess: No media devices support");
		return "failed";
	}
	let probe: Promise<MediaStream> | null = null;
	try {
		probe = navigator.mediaDevices.getUserMedia({ audio: true });
		const stream = await orTimeout(probe, 10_000);
		const audioTrackCount = stream.getAudioTracks().length;
		for (const track of stream.getTracks()) {
			track.stop();
			stream.removeTrack(track);
		}
		return audioTrackCount > 0 ? "granted" : "failed";
	} catch (err) {
		// orTimeout does not cancel the probe: a late resolve would keep the
		// mic busy (tab "in use" indicator), so stop any late tracks. A resolve
		// here can only be the user answering the prompt after the timeout;
		// report it so the caller can recover from the false we return below.
		probe
			?.then((stream) => {
				for (const track of stream.getTracks()) {
					track.stop();
				}
				logger.info("Mic access granted after the prompt timeout");
				options?.onLateGrant?.();
			})
			.catch(() => {});
		if (err instanceof Error && DENIAL_ERROR_NAMES.has(err.name)) {
			// info, not error: mic-blocked users hit this on every attempt.
			logger.info("Mic access denied by getUserMedia");
			return "denied";
		}
		if (err instanceof Error && NO_DEVICE_ERROR_NAMES.has(err.name)) {
			logger.info("Mic access probe found no usable device");
			return "no-device";
		}
		logger.error(err, "Mic access request failed");
		return "failed";
	}
}

/**
 * Enumerate media devices without side effects. Never opens a stream or
 * prompts: before permission is granted this returns an empty list
 * (placeholder entries with empty deviceIds are dropped), and callers
 * decide whether to run requestMicAccess from a user action (BUG-1116).
 * Returns null when enumeration is unsupported or fails.
 */
export async function listDevices(
	kind: MediaDeviceKind | null = "audioinput",
): Promise<MediaDeviceInfo[] | null> {
	if (!hasMediaDevices()) {
		logger.info("listDevices: No media devices support");
		return null;
	}
	const devices = await enumerateDevicesSafe();
	if (!devices) {
		return null;
	}
	const filtered = devices.filter(
		(device) => (!kind || device.kind === kind) && device.deviceId.length > 0,
	);
	filtered.sort((a, b) => {
		if (a.deviceId === "default") {
			return -1;
		}
		if (b.deviceId === "default") {
			return 1;
		}
		if (a.deviceId === "communications") {
			return -1;
		}
		if (b.deviceId === "communications") {
			return 1;
		}
		return a.label.localeCompare(b.label);
	});
	return filtered;
}
